import { defineStore } from 'pinia';
// import { useConditionsStore } from '@experience/store/experience.conditions.store';
import { experienceTypeList } from '@experience/static/config/experience.config';
import type { ECTemplate } from '@models/ecshop/asset';
import type { ExperienceTemplateV4 } from '@models/experience/useExperienceTemplatesV4';
import type { TypeItem } from '@experience/static/config/experience.type';
import EngageV4Apis from '@experience/apis/engageV4List.api';
import useVersionScreenshot from '@experience/hooks/useThumbnail';
import {
    useExperience,
    CreateExperienceV4Input,
    ExperienceVersion,
    ExperienceCreativeTerminal,
    Experience,
    ExperienceType
} from '@models/experience/experience';
import { initExperience } from '@experience/hooks/setting.hooks';
import {
    useECEvent,
    ECDataLocation,
    getDefaultHtml,
    getWidgetNodes
} from '@experience/hooks/template.hooks';
import { getNewVersion, getNewPopup, getControlGroup } from '@experience/views/editor/useEditor';
import { useProfileInfo } from '@models/profile/useCurrentProfile';
import { useAllUserGroups } from '@models/experience/useUserGroups';
import { useGuideStore, GuideKey } from '@/pages/master/store/guide.store';
import router, { route } from '@router/router';
import uuidUtils from '@utils/uuid.utils';
import i18n from '@assets/i18n';
import PtMessage from '@ui/message';
import analyticsService from '@assets/services/analytics.service';

export type ComponentName =
    | 'ExperienceCreateEntry'
    | 'ExperienceTemplate'
    | 'ExperienceForm'
    | 'ExperienceABTestTypeSelect'
    | 'ExperienceNonABTestList'
    | 'ExperienceTemplateBlank';

export type CreateDialog = 'ExperienceForm' | 'ExperienceNonABTestList';
export type EntryPosition =
    | 'cp_list_ab_onbo'
    | 'cp_list'
    | 'cp_onbo'
    | 'web_view_heatmap'
    | 'start_guide';
export type TemplateSource = 'COMMON' | 'OWN';

export interface ComponentData {
    template?: ExperienceTemplateV4 | ECTemplate;
    templateSource?: TemplateSource;
    templateItem?: TypeItem;
    blankTemplate?: {
        id: 'blank';
        editorType: 'popup' | 'inline';
        engageType: 'POPUP' | 'STICKY_BAR';
        position: string;
        stickyMode: 'inject' | 'float';
        height: number;
        hideShadow: 'show' | 'hide';
    };
    visibleBack?: boolean;
    templateListType?: TypeItem;
    experienceNonABTestList?: Experience[];
    templateLocation?: ECDataLocation;
}

export interface DialogData {
    backComponent?: ComponentName | '';
    backComponentData?: ComponentData;
    component?: ComponentName | '';
    componentData?: ComponentData;
}

export interface CreateData {
    visible?: boolean;
    routePath?: string;
    entryPosition?: EntryPosition;
    entryItem?: TypeItem;
    entryComponent?: ComponentName | '';
    experienceType?: ExperienceType;
    dialog?: DialogData;
    fullscreenDialog?: DialogData;
    sid?: string;
    timeZone?: string;
    url?: string;
    pageCondition?: any;
}

export const useCreateDataStore = defineStore('create', {
    state: () => {
        return {
            createLoading: false,
            experienceCreateData: {
                visible: false,
                dialog: {},
                fullscreenDialog: {}
            } as CreateData
        };
    },

    getters: {
        formDialogTitleCode(state) {
            const templateType = state.experienceCreateData.dialog?.componentData?.template?.type;
            switch (state.experienceCreateData.entryItem?.key) {
                case 'POPUP':
                    return 'experience.common.create_dialog_title_step2_popup';
                case 'INLINE':
                    return 'experience.common.create_dialog_title_step2_inline';
                case 'REDIRECT':
                    return 'experience.common.create_dialog_title_step2_redirect';
                case 'ADVANCED':
                    return 'experience.common.create_dialog_title_step2_advanced';
                case 'ABTEST':
                    return 'experience.common.create_dialog_title_step2_abtest';
                default:
                    if (templateType) {
                        if (['POPUP', 'INLINE'].includes(templateType)) {
                            return 'experience.common.create_dialog_title_step2_popup';
                        } else {
                            return 'experience.common.create_dialog_title_step2_inline';
                        }
                    }
                    return '';
            }
        },
        componentData(state) {
            return state.experienceCreateData.dialog?.componentData;
        },
        engageType(state) {
            return state.experienceCreateData?.experienceType;
        },
        isRedirect(state) {
            return state.experienceCreateData?.experienceType === 'REDIRECT';
        },
        createDialogVisible(state) {
            return state.experienceCreateData?.visible;
        }
    },

    actions: {
        updateExperienceCreateData(createData: CreateData) {
            Object.entries(createData).forEach(item => {
                const [key, value] = item;
                if (key === 'routePath' && value) {
                    router.push({
                        query: {
                            create: value
                        }
                    });
                }
                this.experienceCreateData[key as keyof CreateData] = value;
            });
        },

        exitExperienceCreate() {
            this.experienceCreateData = {
                visible: false,
                dialog: {},
                fullscreenDialog: {},
                url: ''
            };
        },

        getExeperienceTemplateType(template: ExperienceTemplateV4 | ECTemplate) {
            const type = template.type === 'COMPONENT' ? 'INLINE' : 'POPUP';
            const experienceType =
                template.type === 'COMPONENT' ? 'INLINE' : (template.type as ExperienceType);
            return {
                entryItem: experienceTypeList.find(item => item.key === type),
                experienceType
            };
        },
        async createExperienceHandler() {
            const { profileInfo } = useProfileInfo();
            const { data: allUsergroups } = useAllUserGroups();
            // const conditionsStore = useConditionsStore();
            this.createLoading = true;
            // const isRedirect = this.engageType === 'REDIRECT';
            const isABTest = ['ABTEST', 'REDIRECT'].includes(
                this.experienceCreateData?.entryItem?.key as string
            );
            const hasBaseUrl = ['INLINE', 'ADVANCED'].includes(this.engageType as string);
            const { id, name, type, tags } = this.componentData?.template || {};

            const experienceId = uuidUtils.uuid();
            const experienceName = i18n.global.t(`experience.common.default_name`);
            const templateSource = id
                ? {
                      id,
                      name,
                      type,
                      purpos: tags?.purpos,
                      industry: tags?.industry
                  }
                : {};
            let versions: ExperienceVersion[] | [] = [];
            const version = await this.getDefaultVersion(experienceId);
            if (isABTest && this.engageType === 'INLINE') {
                // 如果是从ab测创建的Inline默认会创建控制组 rate平均分
                const controlGroup = getControlGroup(experienceId);
                version && (version.rate = 0.5);
                controlGroup.rate = 0.5;
                versions = [version as ExperienceVersion, controlGroup];
            } else if (!this.isRedirect) {
                versions = [version as ExperienceVersion];
            }
            let params: any = initExperience({
                timezone: profileInfo.value?.timeZone,
                commonUsergroupId: allUsergroups.value?.find(usrg => usrg.source === 'default')
                    ?.id as string
            }) as CreateExperienceV4Input;

            params = Object.assign(params, {
                id: experienceId,
                name: experienceName,
                isABTest: isABTest,
                engageType: this.engageType,
                templateSource,
                versions
            });
            hasBaseUrl && (params.baseURL = this.experienceCreateData?.url);

            // const overlayHeatmapConditions = conditionsStore.pageConditions?.conditions;
            // if (overlayHeatmapConditions && this.engageType !== 'REDIRECT') {
            //     params.conditions = overlayHeatmapConditions;
            //     conditionsStore.updatePageConditions(null);
            // }
            if (
                this.experienceCreateData?.pageCondition?.conditions &&
                this.engageType !== 'REDIRECT'
            ) {
                params.conditions = this.experienceCreateData.pageCondition.conditions;
            }
            // inline和advance类型的 默认condition 按合参页面算
            hasBaseUrl &&
                (params.conditions = [
                    {
                        when: [
                            {
                                uuid: uuidUtils.uuid(),
                                type: 'CombinedPage',
                                condition: '==',
                                value: [params.baseURL?.split('?')?.[0]]
                            }
                        ]
                    }
                ]);
            this.isRedirect
                ? this.create(params)
                : useVersionScreenshot(params, () => {
                      this.create(params);
                  });
        },
        async getDefaultVersion(experienceId: string) {
            const newVersion = getNewVersion(experienceId);

            // 仅POPUP及STICKY_BAR有模板
            if (this.engageType === 'POPUP' || this.engageType === 'STICKY_BAR') {
                if (this.componentData?.blankTemplate) {
                    // 默认新增优先按其他版本设备同步，默认不开启移动端
                    const { spTemplate, pcTemplate, type } = this.getDefaultTemplate(
                        this.componentData?.blankTemplate?.position
                    );

                    newVersion.terminals.forEach((terminal: ExperienceCreativeTerminal) => {
                        const deviceType = terminal.type === 1 ? 'mobile' : 'pc';

                        terminal[type] = terminal.type === 1 ? spTemplate : pcTemplate;
                        terminal.type === 1 && (terminal.status = 0);
                        terminal.type === 2 && (terminal.status = 1);
                        terminal[type][0].widgetLayout = getDefaultHtml(
                            type,
                            deviceType,
                            terminal[type][0].widgets[0].widgetSettings
                        );

                        terminal[type][0].widgets[0].widgetNodes = getWidgetNodes(
                            type,
                            terminal[type][0].widgets[0].widgetSettings.position,
                            deviceType,
                            this.engageType === 'POPUP'
                                ? false
                                : terminal.stickybar[0].widgets[0].widgetSettings.hideShadow ===
                                      'hide'
                        );

                        if (this.engageType === 'STICKY_BAR') {
                            if (terminal.type === 1) {
                                terminal.stickybar[0].widgets[0].widgetSettings.height = 180;
                            }

                            if (
                                terminal.stickybar[0].widgets[0].widgetSettings.hideShadow ===
                                'hide'
                            ) {
                                terminal[type][0].widgetLayout = terminal[
                                    type
                                ][0].widgetLayout.replaceAll(
                                    'box-shadow: rgba(0, 0, 0, 0.4) 0px 12px 27px 2px;',
                                    'box-shadow: none;'
                                );
                            }
                        }
                    });
                } else {
                    return await this.processInitByTemplate(
                        this.componentData?.template as ExperienceTemplateV4,
                        newVersion,
                        this.componentData?.templateSource
                    );
                }
            }

            return newVersion;
        },
        getDefaultTemplate(position: string) {
            const type: 'popup' | 'stickybar' = this.engageType === 'POPUP' ? 'popup' : 'stickybar';
            return {
                spTemplate: getNewPopup(
                    type,
                    String(position),
                    i18n.global.t(`experience.setting.${type}`),
                    this.componentData?.blankTemplate
                ),
                pcTemplate: getNewPopup(
                    type,
                    String(position),
                    i18n.global.t(`experience.setting.${type}`),
                    this.componentData?.blankTemplate
                ),
                type
            };
        },
        async processInitByTemplate(
            template: ExperienceTemplateV4,
            newVersion: ExperienceVersion,
            source?: TemplateSource
        ) {
            if (!template) return;
            // 因获取模板接口把pcWidgets、pcWidgetLayout、mobileWidgets和mobileWidgetLayout字段去掉了（为了加快template列表接口速度）。
            // 所以需要单独根据template id 请求template详细配置
            if (
                template?.id &&
                (!template?.pcWidgets ||
                    !template?.pcWidgetLayout ||
                    !template?.mobileWidgets ||
                    !template?.mobileWidgetLayout)
            ) {
                const templateInfo = await EngageV4Apis.getTemplateInfoByID({ id: template.id });
                const { pcWidgetLayout, pcWidgets, mobileWidgets, mobileWidgetLayout } =
                    templateInfo || {};
                templateInfo &&
                    (template = {
                        ...template,
                        pcWidgetLayout,
                        pcWidgets,
                        mobileWidgets,
                        mobileWidgetLayout
                    });
            }

            const { spTemplate, pcTemplate, type } = this.getDefaultTemplate(
                'pos-center',
                this.engageType as string
            );
            const status = [0, 0];

            // 新数据格式中不在存储冗余字段：pcWidgetLayout
            const { pcWidgets, pcWidgetLayout, mobileWidgets, mobileWidgetLayout } = template || {};
            if ((pcWidgets?.[0]?.widgetNodes?.html || pcWidgetLayout) && pcTemplate?.[0]) {
                (pcTemplate[0] as any).widgets = pcWidgets;
                pcTemplate[0].widgetLayout = pcWidgetLayout;
                status[1] = 1;
            }

            // 新数据格式中不在存储冗余字段：mobileWidgetLayout
            if ((mobileWidgets?.[0]?.widgetNodes?.html || mobileWidgetLayout) && spTemplate?.[0]) {
                (spTemplate[0] as any).widgets = mobileWidgets;
                spTemplate[0].widgetLayout = mobileWidgetLayout;
                status[0] = source === 'COMMON' ? 0 : 1;
            }

            // 公共模板，移动端默认关闭
            newVersion.terminals.forEach((terminal: any) => {
                terminal[type] = terminal.type === 1 ? spTemplate : pcTemplate;
                terminal.status = status[terminal.type - 1];
            });
            return newVersion;
        },
        async create(params: any) {
            const { createExperience, updateExperienceUserGuideStatus } = useExperience();
            const { profileInfo } = useProfileInfo();
            createExperience(
                params,
                (experience: Experience) => {
                    if (experience) {
                        analyticsService
                            .usePTX('cp_add_save', {
                                type: this.experienceCreateData.entryItem?.trackKey,
                                source: this.componentData?.blankTemplate ? 'blank' : 'template',
                                position: this.experienceCreateData?.entryPosition,
                                editor_version: 'v4',
                                template_name: this.componentData?.blankTemplate
                                    ? ''
                                    : this.componentData?.template?.name
                            })
                            .track();
                        if (this.componentData?.template && this.componentData?.templateLocation) {
                            useECEvent(
                                'trackCheckout',
                                this.componentData?.template,
                                this.componentData?.templateLocation
                            );
                        }

                        const name = this.isRedirect ? 'ExperienceSetting' : 'ExperienceEditor';
                        const editorType = ['POPUP', 'STICKY_BAR'].includes(this.engageType || '')
                            ? 'popup'
                            : 'inline';
                        const params: any = {
                            experienceId: experience.id,
                            sid: route.value?.params.sid || profileInfo.value?.sid
                        };
                        const query = this.isRedirect
                            ? {}
                            : {
                                  activeType: 'edit',
                                  versionId: experience.versions[0].id,
                                  editorType,
                                  templateId: this.componentData?.template?.id || ''
                              };
                        // 添加type设置页会默认弹出设置框
                        this.isRedirect && (params.type = 'REDIRECT');
                        updateExperienceUserGuideStatus(experience);
                        router
                            .push({
                                name,
                                params,
                                query
                            })
                            .then(() => {
                                this.createLoading = false;
                                this.updateExperienceCreateData({
                                    visible: false
                                });
                            });
                    }
                },
                err => {
                    PtMessage.error(i18n.global.t('form.error.system_error'));
                    setTimeout(() => {
                        this.createLoading = false;
                    }, 500);
                }
            );
        }
    }
});
